import { useState } from "react";
import { Button, Checkbox, Form, InputNumber, Modal, Select, Upload, UploadProps, message } from "antd";
import { saveAs } from 'file-saver';
import { generateColors, shuffleArray, sortColors } from "../utils";

const min = 0;
const max = 1000;

const ColorGenerator = () => {
  const [count, setCount] = useState(20);
  const [randomCount, setRandomCount] = useState(0);
  const [colors, setColors] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [hideUnselected, setHideUnselected] = useState(false);
  const [cols, setCols] = useState<number>(3);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportColors, setExportColors] = useState<string[]>([]);

  const generate = (keepSelected = false) => {
    if (count <= min) return message.warn('颜色数量要大于0');
    if (count > max) return message.warn('颜色数量要小于max');
    if (keepSelected && count <= selectedColors.length) return message.warn('重随未选中颜色时，生成数量要大于已选中的数量');
    const gcs = generateColors(count, keepSelected ? selectedColors : undefined);
    setColors(sortColors(gcs));
    setRandomCount(0);
    setHideUnselected(false);
    if (!keepSelected) setSelectedColors([]);
  };

  const changeSelectedColor = (color: string, selected: boolean) => {
    const sltColors = new Set(selectedColors);
    if (selected) {
      sltColors.add(color);
    } else {
      sltColors.delete(color);
    }
    setSelectedColors(Array.from(sltColors));
  };

  const randomSelect = () => {
    if (randomCount <= 0) return message.warn('随机选择的数量要大于0');
    if (randomCount > colors.length) return message.warn('随机选择的数量要小于颜色总数');
    const sltColors = new Set<string>();
    const tmpSetColors = new Set(colors);
    while (sltColors.size < randomCount) {
      const tmpColors = Array.from(tmpSetColors);
      const index = Math.floor(Math.random() * tmpColors.length);
      tmpSetColors.delete(tmpColors[index]);
      sltColors.add(tmpColors[index]);
    }
    setSelectedColors(Array.from(sltColors));
  };

  const selectAll = () => {
    setSelectedColors([...colors]);
  };

  const unselectAll = () => {
    setSelectedColors([]);
  };

  const returnSelected = () => {
    setSelectedColors(colors.filter(item => !selectedColors.includes(item)));
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(exportColors, null, 2)], { type: 'application/json' });
    saveAs(blob, 'colors.json');
  };

  const exportCsv = () => {
    const csv = exportColors.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    saveAs(blob, 'colors.csv');
  };

  const handleUpload: UploadProps['beforeUpload'] = (file) => {
    try {
      if (file.type !== 'application/json' && file.type !== 'text/csv') throw new Error('上传的文件类型错误，仅接受.json,.csv文件');
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        try {
          if (!reader.result) throw new Error('上从文件中获取的数据为空');
          const s: Set<string> = new Set();
          if (file.type === 'application/json') {
            const json = JSON.parse(reader.result as string);
            if (!Array.isArray(json)) throw new Error('json格式错误');
            json.forEach(item => {
              if (typeof item === 'string' && /^#[0-9a-fA-F]{6}/.test(item)) {
                s.add(item.slice(0, 7).toLowerCase());
              }
            });
          }
          if (file.type === 'text/csv') {
            const csv = reader.result as string;
            csv.split('\n').forEach(item => {
              if (/^#[0-9a-fA-F]{6}/.test(item)) {
                s.add(item.slice(0, 7).toLowerCase());
              }
            });
          }
          if (s.size === 0) throw new Error('文件中颜色数据为空');
          setCount(s.size);
          setColors(sortColors(Array.from(s)));
          setSelectedColors(sortColors(Array.from(s)));
        } catch (e: any) {
          message.warn(e.message);
        }
      }
    } catch(e: any) {
      message.warn(e.message);
    }
    return false;
  };

  const openExportModal = () => {
    setExportColors(sortColors(selectedColors));
    setExportOpen(true);
  };

  const closeExportModal = () => {
    setExportOpen(false);
  };

  const shuffleExportColors = () => {
    setExportColors(shuffleArray(exportColors));
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span>颜色数量: </span>
        <InputNumber
          min={min}
          max={max}
          value={count}
          onChange={value => setCount(value)}
        />
        <Button
          type="primary"
          onClick={() => generate()}
        >
          {colors.length > 0 ? '重新生成' : '生成'}
        </Button>
        <Upload
          accept="application/json,text/csv"
          beforeUpload={handleUpload}
          showUploadList={false}
          maxCount={1}
        >
          <Button
            type="primary"
          >
            {colors.length > 0 ? '重新导入' : '导入'}
          </Button>
        </Upload>
        {selectedColors.length > 0 && count > selectedColors.length && (
          <Button
            type="dashed"
            onClick={() => generate(true)}
          >
            保持选中颜色重随
          </Button>
        )}
      </div>
      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button
          onClick={selectAll}
          disabled={colors.length === selectedColors.length}
        >
          全选
        </Button>
        <Button
          onClick={unselectAll}
          disabled={selectedColors.length === 0}
        >
          全不选
        </Button>
        <Button
          onClick={returnSelected}
          disabled={colors.length === 0}
        >
          反选
        </Button>
        <Form onFinish={randomSelect}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <InputNumber
              min={min}
              max={colors.length}
              value={randomCount}
              onChange={value => setRandomCount(value)}
            />
            <Button
              htmlType="submit"
              disabled={randomCount === 0 || randomCount > colors.length}
            >
              随机选中
            </Button>
          </div>
        </Form>
        <div style={{ userSelect: 'none' }}>
          <label>
            <Checkbox
              checked={hideUnselected}
              onChange={e => setHideUnselected(e.target.checked)}
            />
            <span style={{ marginLeft: 5 }}>隐藏未选中</span>
          </label>
        </div>
        <div style={{ userSelect: 'none' }}>
          <label>
            <span style={{ marginLeft: 5 }}>列数: </span>
            <Select
              style={{ width: 80 }}
              value={cols}
              onChange={value => setCols(value)}
              options={[
                { label: '1', value: 1 }, 
                { label: '2', value: 2 }, 
                { label: '3', value: 3 }, 
                { label: '4', value: 4 }, 
                { label: '5', value: 5 }, 
              ]}
            />
          </label>
        </div>
      </div>
      <div
        style={{
          marginTop: 20,
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 12,
        }}>
        {colors.map(item => {
          const selected = selectedColors.includes(item);
          if (hideUnselected && !selected) return null;
          return (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 20, height: 20, backgroundColor: item }} />
              <div style={{ width: 60 }}>{item}</div>
              <Checkbox
                checked={selected}
                onChange={e => changeSelectedColor(item, e.target.checked)}
              />
            </div>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 20,
          position: 'sticky',
          bottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
        }}
      >
        <span>
          当前选中: {selectedColors.length} 个
        </span>
        <Button
          disabled={selectedColors.length === 0}
          type="primary"
          onClick={openExportModal}
        >
          导出预览
        </Button>
      </div>
      <Modal
        title={`导出预览（${exportColors.length}）`}
        visible={exportOpen}
        width={1000}
        onCancel={closeExportModal}
        bodyStyle={{ height: 400, overflow: 'auto' }}
        footer={(
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <Button
              disabled={exportColors.length === 0}
              onClick={shuffleExportColors}
            >
              打乱
            </Button>
            <Button
              disabled={exportColors.length === 0}
              type="primary"
              onClick={exportJson}
            >
              导出JSON
            </Button>
            <Button
              disabled={exportColors.length === 0}
              type="primary"
              onClick={exportCsv}
            >
              导出CSV
            </Button>
          </div>
        )}
      >
        <div
          style={{
            marginTop: 20,
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 12,
          }}
        >
          {exportColors.map(item => {
            return (
              <div key={item} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <div style={{ width: 20, height: 20, backgroundColor: item }} />
                <div style={{ width: 60 }}>{item}</div>
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

export default ColorGenerator;
